import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { TASKS } from "constants/actions";
import { useAgrochemicalsContext } from "context/AgrochemichalsProvider";
import { useFetchStateHelper } from "hooks/useFetchState";
import { useActionChoices } from "stores/persistent-stores/useActionChoices";

const ENEMIES_PER_PAGE = 5;

export const useStressEnemies = (stressId, enemies = []) => {
  const { actions: allActions } = useActionChoices();
  const { activeSubstances } = useAgrochemicalsContext();
  const drugTaskId = allActions[TASKS.drug]?.taskId;

  // Create stable key from enemy UUIDs
  const enemiesKey = enemies.map((e) => e.uuid).join(",");

  const {
    data,
    isFetchingNextPage,
    hasNextPage,
    isFetchNextPageError,
    fetchNextPage,
    ...restResponse
  } = useInfiniteQuery({
    queryKey: ["stress-enemies-infinite", stressId, enemiesKey],
    queryFn: ({ pageParam }) =>
      api.enemies.fetchStressEnemies(
        enemies,
        drugTaskId,
        activeSubstances,
        ENEMIES_PER_PAGE,
        pageParam,
      ),
    initialPageParam: 0,
    select: (config) => {
      return config.pages.flatMap((page) => page.data);
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
  });

  const { fetchState } = useFetchStateHelper(restResponse);

  return {
    data,
    fetchState,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
  };
};

export const useStressInfo = (stressId, phaseId, options = {}) => {
  const { enabled = true, enemyUuid } = options;
  const { data, ...restResponse } = useQuery({
    queryKey: ["stress-info", stressId, phaseId, enemyUuid],
    queryFn: () =>
      api.enemies.fetchStressInfo({
        stressId,
        phaseId,
        uuid: enemyUuid,
      }),
    enabled: Boolean(enabled && stressId),
    staleTime: 10 * 60 * 1000,
  });

  const { fetchState } = useFetchStateHelper(restResponse);

  return {
    data,
    fetchState,
  };
};

export const useStressOperations = (stressId, phaseId, options = {}) => {
  const { enabled = true, enemyUuid } = options;
  const { data, ...restResponse } = useQuery({
    queryKey: ["stress-operations", stressId, phaseId, enemyUuid],
    queryFn: () =>
      api.enemies.fetchStressOperations({ phaseId, uuid: enemyUuid }),
    enabled: Boolean(enabled && stressId),
    staleTime: 10 * 60 * 1000,
  });

  const { fetchState } = useFetchStateHelper(restResponse);

  return {
    data,
    fetchState,
  };
};
